import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    useLazyGetUsersListQuery,
    useDeleteUserMutation,
    useLazyGetRolesQuery,
} from '../store/api';
import { extractList, extractPagination } from '../utils/response';
import { useDebounce } from '../hooks/useDebounce';
import DashboardDrilldownBanner from './dashboard/DashboardDrilldownBanner';
import { useDashboardDrilldown } from '../hooks/useDashboardDrilldown';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TextField,
    MenuItem,
    Menu,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Button,
    TablePagination,
    Autocomplete
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { useNavigate } from 'react-router-dom';
import { CREATE_USER, DASHBOARD_PATH } from '../navigations/path';
import { isSameUser } from '../store/slices/authSlice';
import SweetAlert from '../utils/sweetAlert';
import { useAuth } from '../hooks/useAuth';
import Can from './permissions/Can';
import { getErrorMessage } from '../utils/errorHandler';

const UserTableList = () => {
    const [fetchUsersList] = useLazyGetUsersListQuery();
    const { params: drilldownParams } = useDashboardDrilldown();
    const [deleteUser] = useDeleteUserMutation();
    const [fetchRoles] = useLazyGetRolesQuery();
    const { user: currentUser, hasPermission, hasAnyPermission, impersonate } = useAuth();

    const canViewUserList = hasAnyPermission(['user-list', 'user-direct-permission']);
    const canEditUser = hasPermission('user-edit');
    const canViewUser = hasPermission('user-view');
    const canDeleteUser = hasPermission('user-delete');

    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);
    const [roleFilterId, setRoleFilterId] = useState(null);
    const [roleOptions, setRoleOptions] = useState([{ id: null, name: 'All Roles' }]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [actionMenuUser, setActionMenuUser] = useState(null);
    const [impersonateLoadingId, setImpersonateLoadingId] = useState(null);

    const [rolesLoading, setRolesLoading] = useState(false);
    const rolesLoadedRef = useRef(false);

    const loadRoles = useCallback(async () => {
        if (rolesLoadedRef.current || rolesLoading) return;
        setRolesLoading(true);
        try {
            const res = await fetchRoles().unwrap();
            const list = extractList(res);
            const mapped = Array.isArray(list)
                ? list.map((r) => ({ id: r.id, name: r.name ?? r.label ?? String(r.id) }))
                : [];
            setRoleOptions([{ id: null, name: 'All Roles' }, ...mapped]);
            rolesLoadedRef.current = true;
        } catch (_) {
            setRoleOptions([{ id: null, name: 'All Roles' }]);
        } finally {
            setRolesLoading(false);
        }
    }, [fetchRoles, rolesLoading]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, roleFilterId]);

    const fetchUsers = useCallback(async () => {
        if (!canViewUserList) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const params = {
                'relations[]': ['status', 'roles'],
                paginate: 1,
                page: page + 1,
                per_page: rowsPerPage,
            };
            const q = debouncedSearch.trim();
            if (q) params.search = q;
            if (roleFilterId) params.role_id = roleFilterId;
            Object.assign(params, drilldownParams);

            const userData = await fetchUsersList(params).unwrap();
            const list = extractList(userData);
            const pag = extractPagination(userData);
            setUsers(Array.isArray(list) ? list : []);
            setTotal(Number(pag.total) || 0);
            setError(null);
        } catch (err) {
            console.error('Fetch users error:', err);
            setError(err?.data?.message || getErrorMessage(err) || 'Failed to load users. Please check your network or API.');
            setUsers([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
        }
    }, [canViewUserList, fetchUsersList, page, rowsPerPage, debouncedSearch, roleFilterId, drilldownParams]);

    useEffect(() => {
        if (!canViewUserList) {
            toast.error('You do not have permission to view users.');
            return;
        }
        fetchUsers();
    }, [canViewUserList, fetchUsers]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const roleAutocompleteValue =
        roleOptions.find((r) => r.id === roleFilterId) ?? roleOptions[0];

    const handleEdit = (userId) => {
        if (!canEditUser) return;
        navigate(`${CREATE_USER}?edit_id=${userId}`);
    };

    const handleView = (userId) => {
        if (!canViewUser) return;
        navigate(`${CREATE_USER}?view=${userId}`);
    };

    const handleDelete = async (userId) => {
        if (!canDeleteUser) return;
        try {
            const targetUser = users.find(u => u.id === userId);
            const result = await SweetAlert.deleteConfirm(targetUser?.name || 'This User');

            // If user cancels, return early
            if (!result.isConfirmed) {
                return;
            }

            await deleteUser(userId).unwrap();
            await SweetAlert.success({
                text: 'Users record deleted successfully.'
            });
            await fetchUsers();
        } catch (error) {
            console.error('Delete action error:', error);
            await SweetAlert.error({
                text: error?.data?.message || getErrorMessage(error) || 'Failed to delete user'
            });
        }
    };

    const openActionMenu = (event, user) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setActionMenuUser(user);
    };

    const closeActionMenu = () => {
        setActionMenuAnchor(null);
        setActionMenuUser(null);
    };

    const handleMenuView = () => {
        if (actionMenuUser) handleView(actionMenuUser.id);
        closeActionMenu();
    };

    const handleMenuEdit = () => {
        if (actionMenuUser) handleEdit(actionMenuUser.id);
        closeActionMenu();
    };

    const handleMenuDelete = () => {
        if (actionMenuUser) handleDelete(actionMenuUser.id);
        closeActionMenu();
    };

    const handleMenuImpersonate = async () => {
        const target = actionMenuUser;
        const targetId = target?.id ?? target?.uuid;
        closeActionMenu();
        if (!targetId) return;
        if (isSameUser(currentUser, target)) {
            toast.warn('You cannot impersonate yourself.');
            return;
        }
        setImpersonateLoadingId(targetId);
        try {
            await impersonate(targetId);
            navigate(DASHBOARD_PATH, { replace: true });
        } catch (err) {
            toast.error(err?.message || 'Failed to impersonate user');
        } finally {
            setImpersonateLoadingId(null);
        }
    };

    const getStatusChip = (status) => {
        const normalizedStatus = status ? status.toLowerCase() : 'inactive';
        const color = normalizedStatus === 'active' ? 'success' : 'error';
        const label = normalizedStatus === 'active' ? 'Active' : 'Inactive';
        return (
            <Chip
                label={label}
                color={color}
                size="small"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
            />
        );
    };

    const formatGender = (gender) => {
        switch (gender) {
            case 'M': return 'Male';
            case 'F': return 'Female';
            case 'O': return 'Other';
            default: return 'Not Specified';
        }
    };

    return (
            <Box sx={{ flexGrow: 1, p: 4, bgcolor: '#fff' }}>
                <DashboardDrilldownBanner />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Users List ({total})
                    </Typography>
                    <Can permission='user-create'>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(CREATE_USER)}
                            disabled={isLoading}
                        >
                            Add New User
                        </Button>
                    </Can>
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    mb: 3,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: 1,
                }}>
                    <TextField
                        label="Search Name or Email"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250 }}
                        disabled={isLoading}
                    />
                    <Autocomplete
                        size="small"
                        options={roleOptions}
                        value={roleAutocompleteValue}
                        onOpen={loadRoles}
                        loading={rolesLoading}
                        onChange={(event, newValue) => {
                            setRoleFilterId(newValue?.id ?? null);
                            setPage(0);
                        }}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        disabled={isLoading}
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Role"
                                sx={{ borderRadius: 2 }}
                            />
                        )}
                        getOptionLabel={(option) => option?.name ?? ''}
                        noOptionsText={rolesLoading ? 'Loading…' : 'No roles'}
                    />
                </Box>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress color="primary" />
                    </Box>
                )}
                {!isLoading && error && (
                    <Typography color="error" align="center" sx={{ my: 4 }}>
                        {error}
                    </Typography>
                )}
                {!isLoading && !error && total === 0 && (
                    <Typography align="center" sx={{ my: 4 }}>
                        No users found matching your criteria.
                    </Typography>
                )}

                {!isLoading && !error && total > 0 && (
                    <Paper elevation={1} sx={{ borderRadius: 2 }}>
                        <TableContainer sx={{ maxHeight: '65vh' }}>
                            <Table stickyHeader aria-label="user list table">
                                <TableHead>
                                    <TableRow sx={{
                                        bgcolor: 'primary.main',
                                        '& th': { color: 'black', fontWeight: 'bold' },
                                    }}>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Sudo Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Gender</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow
                                            key={user.id}
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.sudo_name ?? '-'}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.roles[0]?.name || 'N/A'}</TableCell>
                                            <TableCell>{getStatusChip(user.status?.name)}</TableCell>
                                            <TableCell>{formatGender(user.gender)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Actions"
                                                    onClick={(e) => openActionMenu(e, user)}
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor)}
                            onClose={closeActionMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{ sx: { minWidth: 120 } }}
                        >
                            <Can permission="user-view">
                                <MenuItem onClick={handleMenuView}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>View</ListItemText>
                                </MenuItem>
                            </Can>
                            <Can permission="user-edit">
                                <MenuItem onClick={handleMenuEdit}>
                                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>Edit</ListItemText>
                                </MenuItem>
                            </Can>
                            <Can permission="user-direct-login">
                                <MenuItem
                                    onClick={handleMenuImpersonate}
                                    disabled={
                                        impersonateLoadingId != null ||
                                        isSameUser(currentUser, actionMenuUser)
                                    }
                                >
                                    <ListItemIcon>
                                        {impersonateLoadingId === (actionMenuUser?.id ?? actionMenuUser?.uuid) ? (
                                            <CircularProgress size={18} />
                                        ) : (
                                            <SwitchAccountIcon fontSize="small" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText>Sign in as this user</ListItemText>
                                </MenuItem>
                            </Can>
                            <Can permission="user-delete">
                                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                </MenuItem>
                            </Can>
                        </Menu>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                )}
            </Box>
    );
};

export default UserTableList;
